/**
 * Elite Batch 23 Part 3 — Marketing & Campaign Ops Workflows (36-53)
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "HubSpot: Fetch contact profile", not "API Call")
 * 7. Each workflow needs 8 nodes with proper edges
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 23 PART 3 — Marketing & Campaign Ops (36-53)
// =============================================================================

export const ELITE_BATCH23_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 36. Blog Post Publish to Social Repurposing — Pattern I
  // ===================================================================
  "Blog Post Publish to Social Repurposing": {
    nodes: [
      trigger("CMS Webhook: Blog Post Published", "webhook_app", {
        source: "WordPress",
        event: "post.published",
        howItStarts:
          "Fires when a new blog post is published on your CMS, delivering the post title, body, URL, author, and featured image",
        setup:
          "In your CMS (WordPress/Ghost/Webflow), configure a webhook that fires on the 'post.published' event. Include post title, excerpt, body, URL, author, categories, and featured image URL in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract article details and key takeaways",
        280,
        "You are a content repurposing specialist. Parse the CMS webhook payload and extract the article details needed for social media repurposing. Identify the top 3 takeaways, target audience, and content tone.",
        "Extract from this CMS publish webhook: {{{1.body}}}. Return JSON with: post_title, post_url, author_name, excerpt (max 200 chars), category, key_takeaway_1, key_takeaway_2, key_takeaway_3, target_audience, content_tone (professional/casual/technical).",
        [
          { field: "post_title", as: "post_title", type: "string" },
          { field: "post_url", as: "post_url", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "excerpt", as: "excerpt", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "key_takeaway_1", as: "key_takeaway_1", type: "string" },
          { field: "key_takeaway_2", as: "key_takeaway_2", type: "string" },
          { field: "content_tone", as: "content_tone", type: "string" },
        ],
      ),

      aiNode(
        "3",
        "OpenAI: Generate social variants for LinkedIn, X, and Instagram",
        520,
        "You are a social media copywriter. Given article details and key takeaways, write platform-optimized social posts. LinkedIn: professional with a hook, 150-250 words. X/Twitter: punchy thread opener under 280 chars. Instagram: engaging caption with hashtags.",
        "Article: {post_title}. Key points: {key_takeaway_1}, {key_takeaway_2}. Tone: {content_tone}. URL: {post_url}. Return JSON with: linkedin_post (string), x_post (string under 280 chars), instagram_caption (string with 5 hashtags), suggested_cta (string).",
        [
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "x_post", as: "x_post", type: "string" },
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          { field: "suggested_cta", as: "suggested_cta", type: "string" },
        ],
      ),

      n("4", "runCode", "Check brand guidelines compliance", 760, {
        codeDetails: {
          language: "javascript",
          code: `const linkedin = linkedin_post || ""; const xPost = x_post || ""; const ig = instagram_caption || "";
const bannedWords = ["guaranteed","#1","best ever","unbeatable"];
const violations = bannedWords.filter(w => (linkedin + xPost + ig).toLowerCase().includes(w));
const guideline_pass = violations.length === 0;
const violation_count = violations.length;
const violation_list = violations.join(", ");
return { guideline_pass, violation_count, violation_list };`,
          variables: [
            { name: "guideline_pass", type: "boolean" },
            { name: "violation_count", type: "number" },
            { name: "violation_list", type: "string" },
          ],
        },
      }),

      n(
        "5",
        "approvalGate",
        "Content lead: Review social posts before publishing",
        1000,
        {
          approvalDetails: {
            approvers: ["content-lead@company.com"],
            message:
              "New social posts ready for review.\n\nArticle: {post_title}\nLinkedIn: {linkedin_post}\nX: {x_post}\nInstagram: {instagram_caption}\nBrand violations: {violation_list}\n\nPlease approve or request changes.",
          },
        },
      ),

      n("6", "hitApi", "Buffer: Schedule social posts across platforms", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "profile_ids",
              value:
                "[{linkedin_profile_id},{twitter_profile_id},{instagram_profile_id}]",
            },
            { key: "text", value: "{linkedin_post}" },
            { key: "shorten", value: "true" },
            { key: "now", value: "false" },
          ],
          variables: [
            {
              name: "success",
              customName: "buffer_scheduled",
              type: "boolean",
            },
            {
              name: "updates[0].id",
              customName: "buffer_update_id",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Notify marketing team of scheduled social posts",
        1480,
        ":newspaper: Blog → Social repurpose complete!\n*Article:* {post_title}\n*Author:* {author_name}\n*Posts scheduled via Buffer:* LinkedIn, X, Instagram\n*Brand compliance:* {guideline_pass}\n*CTA:* {suggested_cta}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log social repurposing activity",
        1720,
        "SocialRepurposing",
        '[["{post_title}","{author_name}","{category}","{buffer_scheduled}","{violation_count}","{post_url}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically repurposes published blog posts into platform-optimized social media content. Extracts article details via AI, generates LinkedIn, X, and Instagram variants, checks brand guidelines, routes through content lead approval, schedules posts via Buffer, and logs everything to Google Sheets.",
  },

  // ===================================================================
  // 37. RSS or Newsletter to AI Daily Digest — Pattern I
  // ===================================================================
  "RSS or Newsletter to AI Daily Digest": {
    nodes: [
      trigger("Schedule: Daily RSS Content Fetch at 7AM", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs every day at 7:00 AM to fetch the latest articles from configured RSS feeds and compile an AI-curated digest",
        setup:
          "Configure your RSS feed URLs in the workflow settings. The schedule fires daily at 7AM and fetches items from the past 24 hours across all configured feeds.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "RSS Feed: Fetch latest articles from past 24 hours",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.rss2json.com/v1/api.json?rss_url={rss_feed_url}&api_key={rss2json_key}&count=20",
            headers: [{ key: "Content-Type", value: "application/json" }],
            variables: [
              { name: "items", customName: "feed_items", type: "string" },
              { name: "feed.title", customName: "feed_name", type: "string" },
              {
                name: "items.length",
                customName: "item_count",
                type: "number",
              },
            ],
          },
        },
      ),

      aiNode(
        "3",
        "OpenAI: Summarize top stories and rank by relevance",
        520,
        "You are an industry news curator. Given a list of RSS feed items, identify the top 5 most relevant and impactful stories. Write a brief 2-3 sentence summary for each, highlighting why it matters to our audience.",
        "Analyze these RSS items: {feed_items}. Our industry: B2B SaaS/Marketing. Return JSON with: story_count (number), top_stories (array of {title, summary, url, relevance_score}), digest_headline (catchy one-liner for the digest), total_items_reviewed (number).",
        [
          { field: "story_count", as: "story_count", type: "number" },
          { field: "digest_headline", as: "digest_headline", type: "string" },
          { field: "top_stories", as: "top_stories_json", type: "string" },
          {
            field: "total_items_reviewed",
            as: "total_items_reviewed",
            type: "number",
          },
        ],
      ),

      n("4", "runCode", "Dedupe stories against yesterday's digest", 760, {
        codeDetails: {
          language: "javascript",
          code: `const stories = JSON.parse(top_stories_json || "[]");
const yesterdayUrls = (previous_digest_urls || "").split(",");
const fresh = stories.filter(s => !yesterdayUrls.includes(s.url));
const enough_content = fresh.length >= 3;
const fresh_count = fresh.length;
const digest_body = fresh.map((s, i) => (i+1) + ". " + s.title + "\\n" + s.summary + "\\nRead more: " + s.url).join("\\n\\n");
return { enough_content, fresh_count, digest_body };`,
          variables: [
            { name: "enough_content", type: "boolean" },
            { name: "fresh_count", type: "number" },
            { name: "digest_body", type: "string" },
          ],
        },
      }),

      n("5", "condition", "Enough fresh content for today's digest?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "enough_content", operator: "equals", value: "true" },
          ],
        },
      }),

      emailNode(
        "6",
        "Email: Send curated daily digest to subscribers",
        1240,
        "{digest_subscriber_list}",
        "{digest_headline} — Your Daily Industry Digest",
        "Good morning!\n\nHere are today's top stories curated just for you:\n\n{digest_body}\n\n---\nStories reviewed: {total_items_reviewed} | Fresh picks: {fresh_count}\n\nEnjoy your read!\nYour Marketing Intel Team",
      ),

      slackNode(
        "7",
        "Slack: Post daily digest to #marketing-intel channel",
        1480,
        ":newspaper: *Daily Digest — {digest_headline}*\nFresh stories: {fresh_count} of {total_items_reviewed} reviewed\n\n{digest_body}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log daily digest metrics",
        1720,
        "DailyDigest",
        '[["{feed_name}","{total_items_reviewed}","{fresh_count}","{enough_content}","{digest_headline}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Generates a daily AI-curated content digest from RSS feeds. Fetches latest articles each morning, uses AI to rank and summarize the top stories, deduplicates against previous digests, and delivers a polished email digest to subscribers with a Slack cross-post and Google Sheets logging.",
  },

  // ===================================================================
  // 38. Content Calendar Approval Workflow — Pattern I
  // ===================================================================
  "Content Calendar Approval Workflow": {
    nodes: [
      trigger(
        "Airtable Webhook: Content Draft Ready for Review",
        "webhook_app",
        {
          source: "Airtable",
          event: "record.status_changed",
          howItStarts:
            "Fires when a content calendar entry in Airtable is moved to 'Draft Ready' status, indicating the piece is ready for editorial review",
          setup:
            "In Airtable, set up an automation that sends a webhook when a record's Status field changes to 'Draft Ready'. Include record ID, content title, author, publish date, content type, and draft URL in the payload.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Extract content details and assess readiness",
        280,
        "You are a content operations manager. Parse the Airtable webhook payload to extract content calendar details. Assess readiness for editorial review based on completeness of required fields.",
        "Extract from Airtable webhook: {{{1.body}}}. Return JSON with: record_id, content_title, author_name, author_email, publish_date, content_type (blog/social/email/video), draft_url, target_channel, missing_fields (array of any empty required fields), readiness_score (0-100).",
        [
          { field: "record_id", as: "record_id", type: "string" },
          { field: "content_title", as: "content_title", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "publish_date", as: "publish_date", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "draft_url", as: "draft_url", type: "string" },
          { field: "readiness_score", as: "readiness_score", type: "number" },
        ],
      ),

      n(
        "3",
        "runCode",
        "Check calendar conflicts and publishing slot availability",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `const pubDate = new Date(publish_date || "");
const dayOfWeek = pubDate.getDay();
const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
const has_conflict = isWeekend;
const conflict_reason = isWeekend ? "Publish date falls on a weekend — lower engagement expected" : "No conflicts detected";
const suggested_date = isWeekend ? "Next Tuesday at 9:00 AM" : publish_date;
return { has_conflict, conflict_reason, suggested_date };`,
            variables: [
              { name: "has_conflict", type: "boolean" },
              { name: "conflict_reason", type: "string" },
              { name: "suggested_date", type: "string" },
            ],
          },
        },
      ),

      n(
        "4",
        "approvalGate",
        "Editor: Review content draft and approve for publishing",
        760,
        {
          approvalDetails: {
            approvers: ["editor@company.com"],
            message:
              "Content ready for editorial review:\n\nTitle: {content_title}\nAuthor: {author_name}\nType: {content_type}\nPublish Date: {publish_date}\nDraft: {draft_url}\n\nCalendar: {conflict_reason}\nSuggested date: {suggested_date}\nReadiness: {readiness_score}/100\n\nPlease approve or request revisions.",
          },
        },
      ),

      n("5", "condition", "Was the content approved by the editor?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "approval_status", operator: "equals", value: "approved" },
          ],
        },
      }),

      n("6", "hitApi", "Airtable: Update content status to Approved", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.airtable.com/v0/{base_id}/Content%20Calendar/{record_id}",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"Status":"Approved","Publish Date":"{suggested_date}","Editor Notes":"Approved via workflow"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_record_id", type: "string" },
            { name: "fields.Status", customName: "new_status", type: "string" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Notify content team of approval decision",
        1480,
        ":white_check_mark: *Content Approved*\n*Title:* {content_title}\n*Author:* {author_name}\n*Type:* {content_type}\n*Publish Date:* {suggested_date}\n*Draft:* {draft_url}\n*Status:* {new_status}",
      ),

      emailNode(
        "8",
        "Email: Confirm approval to author",
        1720,
        "{author_email}",
        "Your content '{content_title}' has been approved!",
        "Hi {author_name},\n\nGreat news — your {content_type} titled '{content_title}' has been reviewed and approved for publishing.\n\nPublish Date: {suggested_date}\nCalendar Note: {conflict_reason}\n\nIf you need to make any last-minute changes, please update the draft before the scheduled publish date.\n\nThank you for your contribution!\nThe Editorial Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Orchestrates content calendar approval when drafts are marked ready in Airtable. Extracts content details via AI, checks for calendar conflicts, routes through editorial approval, updates the Airtable record status, and notifies the team via Slack and the author via email.",
  },

  // ===================================================================
  // 39. Google Ads Lead Sync to Sheets or CRM — Pattern K
  // ===================================================================
  "Google Ads Lead Sync to Sheets or CRM": {
    nodes: [
      trigger("Google Ads Webhook: Lead Form Submitted", "webhook_app", {
        source: "Google Ads",
        event: "lead_form.submitted",
        howItStarts:
          "Fires when a prospect submits a lead form extension on a Google Ads campaign, capturing their contact details and campaign attribution data",
        setup:
          "In Google Ads > Assets > Lead Forms, configure the webhook delivery option to POST lead submissions to your endpoint. Include all form fields, campaign ID, ad group ID, and click ID (gclid) in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract lead data and campaign attribution",
        280,
        "You are a paid media lead processor. Parse Google Ads lead form webhook payload. Extract all contact fields, identify the campaign, and assess lead quality based on form responses.",
        "Extract from Google Ads lead webhook: {{{1.body}}}. Return JSON with: lead_email, lead_name, lead_phone, company_name, campaign_id, campaign_name, ad_group, gclid, form_responses (string summary), lead_quality (high/medium/low).",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "gclid", as: "gclid", type: "string" },
          { field: "lead_quality", as: "lead_quality", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Search for existing contact by email", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{lead_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "total",
              customName: "existing_contact_count",
              type: "number",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
          ],
        },
      }),

      n(
        "4",
        "runCode",
        "Validate UTM attribution and tag campaign source",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const hasGclid = (gclid || "").length > 10;
const utm_valid = hasGclid;
const attribution_source = hasGclid ? "Google Ads" : "Unknown";
const is_duplicate = parseInt(existing_contact_count || "0") > 0;
const campaign_tag = "gads_" + (campaign_name || "unknown").replace(/\\s+/g, "_").toLowerCase();
return { utm_valid, attribution_source, is_duplicate, campaign_tag };`,
            variables: [
              { name: "utm_valid", type: "boolean" },
              { name: "attribution_source", type: "string" },
              { name: "is_duplicate", type: "boolean" },
              { name: "campaign_tag", type: "string" },
            ],
          },
        },
      ),

      n("5", "condition", "Is this a duplicate lead already in CRM?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_duplicate", operator: "equals", value: "true" },
          ],
        },
      }),

      sheetsNode(
        "6",
        "Google Sheets: Append lead to Google Ads leads tracker",
        1240,
        "GoogleAdsLeads",
        '[["{lead_name}","{lead_email}","{company_name}","{campaign_name}","{lead_quality}","{attribution_source}","{is_duplicate}","{gclid}","=NOW()"]]',
      ),

      slackNode(
        "7",
        "Slack: Alert sales team of new Google Ads lead",
        1480,
        ":dart: *New Google Ads Lead*\n*Name:* {lead_name}\n*Email:* {lead_email}\n*Company:* {company_name}\n*Campaign:* {campaign_name}\n*Quality:* {lead_quality}\n*Duplicate:* {is_duplicate}\n*Attribution:* {attribution_source}",
      ),

      n(
        "8",
        "hitApi",
        "HubSpot: Create or tag contact with campaign attribution",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{lead_email}","firstname":"{lead_name}","company":"{company_name}","phone":"{lead_phone}","hs_lead_status":"NEW","utm_campaign":"{campaign_tag}","google_click_id":"{gclid}"}',
              },
            ],
            variables: [
              { name: "id", customName: "created_contact_id", type: "string" },
              {
                name: "properties.hs_lead_status",
                customName: "lead_status",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Syncs Google Ads lead form submissions to HubSpot CRM and Google Sheets. Extracts lead data and attribution via AI, checks for duplicate contacts, validates UTM/gclid tracking, logs leads to a tracking spreadsheet, alerts sales via Slack, and creates or tags the contact in HubSpot with full campaign attribution.",
  },

  // ===================================================================
  // 40. Offline Conversion Tracking Back to Ad Platforms — Pattern K
  // ===================================================================
  "Offline Conversion Tracking Back to Ad Platforms": {
    nodes: [
      trigger("HubSpot Webhook: Deal Marked as Won", "webhook_app", {
        source: "HubSpot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal in HubSpot is moved to 'Closed Won' status, triggering offline conversion upload back to Google Ads and Facebook Ads for accurate ROAS measurement",
        setup:
          "In HubSpot > Workflows or webhook subscriptions, trigger on deal stage change to 'closedwon'. Include deal ID, amount, close date, associated contact email, and any stored gclid or fbclid values.",
        inputFields: [],
      }),

      n("2", "runCode", "Map deal to original ad click IDs", 280, {
        codeDetails: {
          language: "javascript",
          code: `const body = JSON.parse(trigger_body || "{}");
const deal_id = body.objectId || body.deal_id || "";
const deal_amount = body.properties?.amount?.value || body.amount || "0";
const close_date = body.properties?.closedate?.value || new Date().toISOString();
const contact_email = body.properties?.contact_email || body.email || "";
const gclid = body.properties?.google_click_id || "";
const fbclid = body.properties?.facebook_click_id || "";
const has_gclid = gclid.length > 5;
const has_fbclid = fbclid.length > 5;
return { deal_id, deal_amount, close_date, contact_email, gclid, fbclid, has_gclid, has_fbclid };`,
          variables: [
            { name: "deal_id", type: "string" },
            { name: "deal_amount", type: "string" },
            { name: "close_date", type: "string" },
            { name: "gclid", type: "string" },
            { name: "fbclid", type: "string" },
            { name: "has_gclid", type: "boolean" },
            { name: "has_fbclid", type: "boolean" },
          ],
        },
      }),

      n(
        "3",
        "hitApi",
        "Google Ads: Upload offline conversion with gclid",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}:uploadClickConversions",
            headers: [
              { key: "Authorization", value: "Bearer {google_ads_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "developer-token", value: "{google_ads_dev_token}" },
            ],
            body: [
              {
                key: "conversions",
                value:
                  '[{"gclid":"{gclid}","conversionAction":"customers/{google_ads_customer_id}/conversionActions/{conversion_action_id}","conversionDateTime":"{close_date}","conversionValue":{deal_amount},"currencyCode":"USD"}]',
              },
            ],
            variables: [
              {
                name: "results[0].gclid",
                customName: "gads_uploaded_gclid",
                type: "string",
              },
              {
                name: "partialFailureError",
                customName: "gads_error",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "hitApi", "Facebook CAPI: Send offline purchase event", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{fb_pixel_id}/events",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            {
              key: "data",
              value:
                '[{"event_name":"Purchase","event_time":"{close_date}","user_data":{"em":"{contact_email}","fbc":"{fbclid}"},"custom_data":{"value":"{deal_amount}","currency":"USD"}}]',
            },
            { key: "access_token", value: "{fb_capi_token}" },
          ],
          variables: [
            {
              name: "events_received",
              customName: "fb_events_received",
              type: "number",
            },
            { name: "fbtrace_id", customName: "fb_trace_id", type: "string" },
          ],
        },
      }),

      n(
        "5",
        "condition",
        "Was attribution matched to at least one ad platform?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "has_gclid", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      sheetsNode(
        "6",
        "Google Sheets: Log offline conversion attribution",
        1240,
        "OfflineConversions",
        '[["{deal_id}","{deal_amount}","{contact_email}","{has_gclid}","{has_fbclid}","{gads_uploaded_gclid}","{fb_events_received}","{close_date}","=NOW()"]]',
      ),

      slackNode(
        "7",
        "Slack: Report offline conversion sync status",
        1480,
        ":moneybag: *Offline Conversion Synced*\n*Deal:* {deal_id} — ${deal_amount}\n*Google Ads:* {has_gclid} (gclid: {gclid})\n*Facebook:* {has_fbclid} (events received: {fb_events_received})\n*Close date:* {close_date}",
      ),

      n(
        "8",
        "runCode",
        "Reconcile attribution gaps and flag mismatches",
        1720,
        {
          codeDetails: {
            language: "javascript",
            code: `const gadsOk = has_gclid && !gads_error;
const fbOk = has_fbclid && parseInt(fb_events_received || "0") > 0;
const fully_attributed = gadsOk || fbOk;
const attribution_gap = !has_gclid && !has_fbclid;
const reconcile_status = attribution_gap ? "NO_CLICK_ID" : fully_attributed ? "MATCHED" : "PARTIAL_MATCH";
return { fully_attributed, attribution_gap, reconcile_status };`,
            variables: [
              { name: "fully_attributed", type: "boolean" },
              { name: "attribution_gap", type: "boolean" },
              { name: "reconcile_status", type: "string" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Closes the attribution loop by syncing CRM closed-won deals back to Google Ads and Facebook as offline conversions. Maps deal data to original click IDs, uploads conversions to both ad platforms, logs attribution status, and reconciles any gaps for accurate ROAS reporting.",
  },

  // ===================================================================
  // 41. UTM Governance Checker — Pattern K
  // ===================================================================
  "UTM Governance Checker": {
    nodes: [
      trigger("Schedule: Daily UTM Compliance Audit", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9:00 AM to audit recent HubSpot leads for UTM parameter compliance, flagging non-standard or missing attribution tags",
        setup:
          "Schedule runs daily. Ensure HubSpot API token has read access to contacts. Define your UTM naming conventions (lowercase, underscore-separated, approved source/medium values) in the runCode node.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "HubSpot: Fetch leads created in the last 24 hours",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"createdate","operator":"GTE","value":"{yesterday_epoch}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["email","utm_source","utm_medium","utm_campaign","utm_content","utm_term","hs_analytics_source"]',
              },
              { key: "limit", value: "100" },
            ],
            variables: [
              { name: "total", customName: "total_leads", type: "number" },
              { name: "results", customName: "lead_results", type: "string" },
            ],
          },
        },
      ),

      n("3", "runCode", "Validate UTM format against naming conventions", 520, {
        codeDetails: {
          language: "javascript",
          code: `const leads = JSON.parse(lead_results || "[]");
const validSources = ["google","facebook","linkedin","twitter","email","direct","organic","referral"];
const validMediums = ["cpc","cpm","social","email","organic","referral","display","video"];
const violations = [];
leads.forEach(l => {
  const p = l.properties || {};
  const src = (p.utm_source || "").toLowerCase();
  const med = (p.utm_medium || "").toLowerCase();
  if (src && !validSources.includes(src)) violations.push({email: p.email, field: "utm_source", value: src, issue: "non-standard source"});
  if (med && !validMediums.includes(med)) violations.push({email: p.email, field: "utm_medium", value: med, issue: "non-standard medium"});
  if (p.utm_campaign && p.utm_campaign !== p.utm_campaign.toLowerCase()) violations.push({email: p.email, field: "utm_campaign", value: p.utm_campaign, issue: "not lowercase"});
  if (!p.utm_source && !p.utm_medium) violations.push({email: p.email, field: "all_utm", value: "empty", issue: "missing UTM params"});
});
const violation_count = violations.length;
const violations_found = violation_count > 0;
const violation_summary = violations.slice(0,10).map(v => v.email + ": " + v.field + "=" + v.value + " (" + v.issue + ")").join("\\n");
return { violation_count, violations_found, violation_summary };`,
          variables: [
            { name: "violation_count", type: "number" },
            { name: "violations_found", type: "boolean" },
            { name: "violation_summary", type: "string" },
          ],
        },
      }),

      aiNode(
        "4",
        "OpenAI: Analyze violation patterns and suggest corrections",
        760,
        "You are a marketing operations UTM governance specialist. Analyze UTM violations and identify patterns — which campaigns, sources, or team members are generating non-compliant tags. Suggest specific corrections.",
        "UTM violations found: {violation_summary}. Total leads: {total_leads}. Violation count: {violation_count}. Return JSON with: pattern_analysis (string describing common issues), top_offender (most frequent violation type), recommended_fix (actionable next step), severity (critical/warning/info).",
        [
          { field: "pattern_analysis", as: "pattern_analysis", type: "string" },
          { field: "top_offender", as: "top_offender", type: "string" },
          { field: "recommended_fix", as: "recommended_fix", type: "string" },
          { field: "severity", as: "severity", type: "string" },
        ],
      ),

      n("5", "condition", "Were UTM violations found in today's leads?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "violations_found", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert marketing ops of UTM violations",
        1240,
        ":warning: *UTM Governance Alert*\n*Violations:* {violation_count} of {total_leads} leads\n*Severity:* {severity}\n*Top Issue:* {top_offender}\n*Pattern:* {pattern_analysis}\n*Fix:* {recommended_fix}\n\nDetails:\n{violation_summary}",
      ),

      emailNode(
        "7",
        "Email: Send UTM violation report to marketing ops",
        1480,
        "marketing-ops@company.com",
        "UTM Governance Report — {violation_count} violations detected",
        "Daily UTM Compliance Report\n\nTotal leads audited: {total_leads}\nViolations found: {violation_count}\nSeverity: {severity}\n\nTop offender: {top_offender}\nPattern analysis: {pattern_analysis}\nRecommended fix: {recommended_fix}\n\nDetailed violations:\n{violation_summary}\n\nPlease review and correct campaign tags to maintain attribution accuracy.\n\nMarketing Ops Automation",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log daily UTM audit results",
        1720,
        "UTMAudit",
        '[["{total_leads}","{violation_count}","{severity}","{top_offender}","{recommended_fix}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Performs daily UTM governance audits on HubSpot leads. Validates UTM parameters against naming conventions, uses AI to analyze violation patterns and recommend fixes, alerts marketing ops via Slack and email when non-compliant tags are found, and logs audit results for trend tracking.",
  },

  // ===================================================================
  // 42. Brand Mention and Sentiment Tracker — Pattern L
  // ===================================================================
  "Brand Mention and Sentiment Tracker": {
    nodes: [
      trigger("Schedule: Hourly Brand Mention Scan", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to scan for new brand mentions across news, blogs, forums, and social media using the Mention API, then analyzes sentiment trends",
        setup:
          "Configure your brand name, competitors, and keywords in the Mention API alert. Schedule fires hourly. Ensure Mention API key is set and alert ID is configured for your brand.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "Mention API: Fetch new brand mentions from last hour",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.mention.com/api/accounts/{mention_account_id}/alerts/{alert_id}/mentions?since_id={last_mention_id}&limit=50",
            headers: [
              { key: "Authorization", value: "Bearer {mention_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            variables: [
              { name: "mentions", customName: "raw_mentions", type: "string" },
              {
                name: "mentions.length",
                customName: "mention_count",
                type: "number",
              },
            ],
          },
        },
      ),

      aiNode(
        "3",
        "OpenAI: Classify sentiment for each mention",
        520,
        "You are a brand sentiment analyst. Classify each brand mention as positive, negative, or neutral. Identify the source type, reach estimate, and key themes. Flag any mentions that could indicate a PR crisis.",
        "Analyze these brand mentions: {raw_mentions}. Return JSON with: positive_count (number), negative_count (number), neutral_count (number), sentiment_trend (improving/stable/declining), crisis_flag (boolean), top_negative_mention (string summary of most concerning negative mention), key_themes (comma-separated list).",
        [
          { field: "positive_count", as: "positive_count", type: "number" },
          { field: "negative_count", as: "negative_count", type: "number" },
          { field: "neutral_count", as: "neutral_count", type: "number" },
          { field: "sentiment_trend", as: "sentiment_trend", type: "string" },
          { field: "crisis_flag", as: "crisis_flag", type: "boolean" },
          {
            field: "top_negative_mention",
            as: "top_negative_mention",
            type: "string",
          },
          { field: "key_themes", as: "key_themes", type: "string" },
        ],
      ),

      n("4", "runCode", "Score mention volume trend and detect spikes", 760, {
        codeDetails: {
          language: "javascript",
          code: `const total = parseInt(mention_count || "0");
const negatives = parseInt(negative_count || "0");
const negativeRatio = total > 0 ? negatives / total : 0;
const negative_spike = negativeRatio > 0.3 && negatives >= 3;
const volume_score = total > 20 ? "high" : total > 5 ? "medium" : "low";
const alert_level = negative_spike ? "URGENT" : crisis_flag ? "HIGH" : "NORMAL";
return { negative_spike, volume_score, alert_level };`,
          variables: [
            { name: "negative_spike", type: "boolean" },
            { name: "volume_score", type: "string" },
            { name: "alert_level", type: "string" },
          ],
        },
      }),

      n(
        "5",
        "condition",
        "Is there a negative sentiment spike or crisis?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "negative_spike", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Urgent alert — negative brand mention spike detected",
        1240,
        ":rotating_light: *URGENT: Negative Brand Mention Spike*\n*Alert Level:* {alert_level}\n*Mentions this hour:* {mention_count}\n*Sentiment:* +{positive_count} / -{negative_count} / ~{neutral_count}\n*Trend:* {sentiment_trend}\n*Top concern:* {top_negative_mention}\n*Themes:* {key_themes}",
      ),

      emailNode(
        "7",
        "Email: PR team — brand sentiment summary",
        1480,
        "pr-team@company.com",
        "Brand Mention Alert — {alert_level}: {mention_count} mentions detected",
        "Hourly Brand Mention Report\n\nAlert Level: {alert_level}\nTotal Mentions: {mention_count}\nVolume: {volume_score}\n\nSentiment Breakdown:\n- Positive: {positive_count}\n- Negative: {negative_count}\n- Neutral: {neutral_count}\n- Trend: {sentiment_trend}\n\nTop Negative Mention:\n{top_negative_mention}\n\nKey Themes: {key_themes}\n\nPlease review and take appropriate action if needed.\n\nBrand Monitoring Bot",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log hourly sentiment metrics",
        1720,
        "BrandSentiment",
        '[["{mention_count}","{positive_count}","{negative_count}","{neutral_count}","{sentiment_trend}","{alert_level}","{key_themes}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors brand mentions hourly across the web using the Mention API. AI classifies sentiment, detects volume spikes and crisis signals, triggers urgent Slack alerts for negative spikes, sends PR team email summaries, and logs all sentiment metrics to Google Sheets for trend analysis.",
  },

  // ===================================================================
  // 43. Review Monitoring + Routing — Pattern K
  // ===================================================================
  "Review Monitoring + Routing": {
    nodes: [
      trigger("G2/Trustpilot Webhook: New Review Posted", "webhook_app", {
        source: "G2",
        event: "review.created",
        howItStarts:
          "Fires when a new review is posted on G2, Trustpilot, or Capterra, delivering the review rating, text, reviewer details, and product information",
        setup:
          "Configure G2 or Trustpilot webhook integration to POST new reviews to your endpoint. Include reviewer name, email, rating (1-5), title, body text, product name, and review URL in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract review rating and identify key themes",
        280,
        "You are a customer review analyst. Parse the review webhook and extract structured details. Identify the sentiment, key themes (product quality, support, pricing, features), and whether immediate follow-up is needed.",
        "Extract from this review webhook: {{{1.body}}}. Return JSON with: reviewer_name, reviewer_email, rating (1-5 number), review_title, review_summary (max 200 chars), platform (G2/Trustpilot/Capterra), key_themes (comma-separated), needs_response (boolean), urgency (high/medium/low).",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "reviewer_email", as: "reviewer_email", type: "string" },
          { field: "rating", as: "rating", type: "number" },
          { field: "review_title", as: "review_title", type: "string" },
          { field: "review_summary", as: "review_summary", type: "string" },
          { field: "platform", as: "platform", type: "string" },
          { field: "key_themes", as: "key_themes", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Match reviewer to existing customer record",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{reviewer_email}"}]}]',
              },
            ],
            variables: [
              {
                name: "total",
                customName: "customer_match_count",
                type: "number",
              },
              {
                name: "results[0].properties.hs_object_id",
                customName: "customer_id",
                type: "string",
              },
              {
                name: "results[0].properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "runCode",
        "Route review by rating and determine response action",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const stars = parseInt(rating || "3");
const is_negative = stars <= 2;
const is_positive = stars >= 4;
const route_to = is_negative ? "support" : is_positive ? "marketing" : "cs";
const response_priority = is_negative ? "urgent" : "standard";
const needs_recovery = is_negative && lifecycle_stage === "customer";
return { is_negative, is_positive, route_to, response_priority, needs_recovery };`,
            variables: [
              { name: "is_negative", type: "boolean" },
              { name: "is_positive", type: "boolean" },
              { name: "route_to", type: "string" },
              { name: "response_priority", type: "string" },
              { name: "needs_recovery", type: "boolean" },
            ],
          },
        },
      ),

      n("5", "condition", "Is this a 1-2 star negative review?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_negative", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert support team of negative review",
        1240,
        ":warning: *Negative Review Alert — {rating}/5 Stars*\n*Platform:* {platform}\n*Reviewer:* {reviewer_name}\n*Title:* {review_title}\n*Summary:* {review_summary}\n*Themes:* {key_themes}\n*Priority:* {response_priority}\n*Route to:* {route_to}\n*Needs recovery:* {needs_recovery}",
      ),

      emailNode(
        "7",
        "Email: Recovery outreach to dissatisfied reviewer",
        1480,
        "{reviewer_email}",
        "Thank you for your feedback, {reviewer_name} — we want to make this right",
        "Hi {reviewer_name},\n\nThank you for taking the time to leave your review on {platform}. We take your feedback seriously, and we're sorry your experience didn't meet expectations.\n\nKey themes you raised: {key_themes}\n\nWe'd love to address your concerns directly. A member of our team will reach out within 24 hours to discuss how we can improve your experience.\n\nIn the meantime, please don't hesitate to reach out at support@company.com.\n\nSincerely,\nCustomer Success Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log review details and routing action",
        1720,
        "ReviewMonitoring",
        '[["{platform}","{reviewer_name}","{rating}","{review_title}","{key_themes}","{route_to}","{response_priority}","{needs_recovery}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("5", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Monitors new reviews on G2, Trustpilot, and Capterra. Extracts review details and sentiment via AI, matches the reviewer to CRM records in HubSpot, routes negative reviews to support with urgent Slack alerts and recovery outreach emails, and logs all review activity to Google Sheets for trend analysis.",
  },

  // ===================================================================
  // 44. SEO Audit Report Generator — Pattern I
  // ===================================================================
  "SEO Audit Report Generator": {
    nodes: [
      trigger("Schedule: Weekly SEO Audit Every Monday", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs every Monday at 8:00 AM to pull SEO metrics from Ahrefs and Google Search Console, generate an AI analysis of gaps and opportunities, and distribute the report",
        setup:
          "Configure Ahrefs API token and Google Search Console API access. Schedule fires weekly on Mondays. Define your target domain and key competitor domains in the API request URLs.",
        inputFields: [],
      }),

      n("2", "hitApi", "Ahrefs: Fetch site metrics and backlink profile", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.ahrefs.com/v3/site-explorer/metrics?target={target_domain}&mode=domain",
          headers: [
            { key: "Authorization", value: "Bearer {ahrefs_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          variables: [
            {
              name: "metrics.org_traffic",
              customName: "organic_traffic",
              type: "number",
            },
            {
              name: "metrics.org_keywords",
              customName: "ranking_keywords",
              type: "number",
            },
            {
              name: "metrics.backlinks",
              customName: "total_backlinks",
              type: "number",
            },
            {
              name: "metrics.domain_rating",
              customName: "domain_rating",
              type: "number",
            },
            {
              name: "metrics.org_traffic_value",
              customName: "traffic_value",
              type: "number",
            },
          ],
        },
      }),

      n(
        "3",
        "hitApi",
        "Google Search Console: Fetch ranking and click data",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/webmasters/v3/sites/{site_url}/searchAnalytics/query",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "startDate", value: "{seven_days_ago}" },
              { key: "endDate", value: "{yesterday}" },
              { key: "dimensions", value: '["query","page"]' },
              { key: "rowLimit", value: "50" },
            ],
            variables: [
              { name: "rows", customName: "search_data", type: "string" },
              {
                name: "responseAggregationType",
                customName: "aggregation_type",
                type: "string",
              },
            ],
          },
        },
      ),

      aiNode(
        "4",
        "OpenAI: Analyze SEO gaps and identify growth opportunities",
        760,
        "You are an SEO strategist. Analyze Ahrefs site metrics and Google Search Console data to identify SEO gaps, declining keywords, quick-win opportunities, and content recommendations. Prioritize by impact and effort.",
        "Ahrefs metrics — Organic traffic: {organic_traffic}, Keywords: {ranking_keywords}, Backlinks: {total_backlinks}, DR: {domain_rating}, Traffic value: ${traffic_value}. GSC data: {search_data}. Return JSON with: overall_health (good/moderate/needs_attention), top_opportunity (string), declining_keywords (string), quick_wins (string describing 3 actionable items), content_gap (string), recommended_priority (string).",
        [
          { field: "overall_health", as: "overall_health", type: "string" },
          { field: "top_opportunity", as: "top_opportunity", type: "string" },
          {
            field: "declining_keywords",
            as: "declining_keywords",
            type: "string",
          },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          { field: "content_gap", as: "content_gap", type: "string" },
          {
            field: "recommended_priority",
            as: "recommended_priority",
            type: "string",
          },
        ],
      ),

      n("5", "runCode", "Format SEO audit report for distribution", 1000, {
        codeDetails: {
          language: "javascript",
          code: `const report = [
  "=== WEEKLY SEO AUDIT REPORT ===",
  "Domain Rating: " + domain_rating,
  "Organic Traffic: " + organic_traffic,
  "Ranking Keywords: " + ranking_keywords,
  "Backlinks: " + total_backlinks,
  "Traffic Value: $" + traffic_value,
  "",
  "Health: " + overall_health,
  "Top Opportunity: " + top_opportunity,
  "Declining Keywords: " + declining_keywords,
  "Quick Wins: " + quick_wins,
  "Content Gap: " + content_gap,
  "Priority: " + recommended_priority
].join("\\n");
return { formatted_report: report, report_generated: true };`,
          variables: [
            { name: "formatted_report", type: "string" },
            { name: "report_generated", type: "boolean" },
          ],
        },
      }),

      emailNode(
        "6",
        "Email: Send weekly SEO audit to content team",
        1240,
        "content-team@company.com",
        "Weekly SEO Audit Report — Health: {overall_health}",
        "Hi Content Team,\n\nHere's your weekly SEO performance summary:\n\n{formatted_report}\n\nKey Action Items:\n1. {top_opportunity}\n2. Address declining keywords: {declining_keywords}\n3. Quick wins: {quick_wins}\n\nLet's discuss priorities in our Monday standup.\n\nSEO Automation Bot",
      ),

      slackNode(
        "7",
        "Slack: Post weekly SEO digest to #seo-channel",
        1480,
        ":mag: *Weekly SEO Digest*\n*Health:* {overall_health}\n*DR:* {domain_rating} | *Traffic:* {organic_traffic} | *Keywords:* {ranking_keywords}\n*Top Opportunity:* {top_opportunity}\n*Quick Wins:* {quick_wins}\n*Priority:* {recommended_priority}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log weekly SEO metrics for trending",
        1720,
        "SEOAudit",
        '[["{domain_rating}","{organic_traffic}","{ranking_keywords}","{total_backlinks}","{traffic_value}","{overall_health}","{top_opportunity}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Generates a comprehensive weekly SEO audit by pulling metrics from Ahrefs and Google Search Console. AI analyzes gaps, declining keywords, and quick-win opportunities, then formats and distributes the report to the content team via email and Slack, with historical metrics logged to Google Sheets.",
  },

  // ===================================================================
  // 45. Keyword Research Brief Builder — Pattern K
  // ===================================================================
  "Keyword Research Brief Builder": {
    nodes: [
      trigger(
        "Webhook Form: Topic Research Request Submitted",
        "webhook_form",
        {
          source: "Internal",
          event: "form.submitted",
          howItStarts:
            "Fires when a content strategist or product marketer submits a keyword research request form with a topic, target audience, and business objective",
          setup:
            "Create an internal form (Outgrow Forms/Outgrow Form) that collects: topic seed keyword, target audience, content goal (traffic/leads/authority), and any competitor URLs. Configure the form to POST submissions to the webhook endpoint.",
          inputFields: [],
        },
      ),

      n("2", "hitApi", "Ahrefs: Pull keyword data for seed topic", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.ahrefs.com/v3/keywords-explorer/keyword-ideas?keyword={seed_keyword}&country=us&limit=30",
          headers: [
            { key: "Authorization", value: "Bearer {ahrefs_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          variables: [
            { name: "keywords", customName: "keyword_data", type: "string" },
            {
              name: "keywords.length",
              customName: "keyword_count",
              type: "number",
            },
          ],
        },
      }),

      aiNode(
        "3",
        "OpenAI: Analyze search intent and competitive landscape",
        520,
        "You are an SEO keyword strategist. Analyze keyword data to determine search intent clusters, competitive difficulty, and content format recommendations. Group keywords by intent (informational, commercial, transactional, navigational).",
        "Keyword data for topic research: {keyword_data}. Seed keyword from form: {{{1.body}}}. Return JSON with: primary_keyword (highest opportunity), search_volume (number), keyword_difficulty (number 0-100), search_intent (informational/commercial/transactional), content_format (blog/guide/comparison/tool), related_keywords (comma-separated top 5), competitive_gap (string describing opportunity).",
        [
          { field: "primary_keyword", as: "primary_keyword", type: "string" },
          { field: "search_volume", as: "search_volume", type: "number" },
          {
            field: "keyword_difficulty",
            as: "keyword_difficulty",
            type: "number",
          },
          { field: "search_intent", as: "search_intent", type: "string" },
          { field: "content_format", as: "content_format", type: "string" },
          { field: "related_keywords", as: "related_keywords", type: "string" },
          { field: "competitive_gap", as: "competitive_gap", type: "string" },
        ],
      ),

      n(
        "4",
        "runCode",
        "Score keyword opportunity by volume, difficulty, and intent",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const vol = parseInt(search_volume || "0");
const diff = parseInt(keyword_difficulty || "50");
const intentMultiplier = search_intent === "commercial" ? 1.5 : search_intent === "transactional" ? 2.0 : 1.0;
const opportunity_score = Math.round((vol / 100) * (100 - diff) * intentMultiplier / 10);
const is_high_value = opportunity_score > 50;
const priority = is_high_value ? "HIGH" : opportunity_score > 25 ? "MEDIUM" : "LOW";
return { opportunity_score, is_high_value, priority };`,
            variables: [
              { name: "opportunity_score", type: "number" },
              { name: "is_high_value", type: "boolean" },
              { name: "priority", type: "string" },
            ],
          },
        },
      ),

      n("5", "condition", "Is this a high-value keyword opportunity?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_high_value", operator: "equals", value: "true" },
          ],
        },
      }),

      n(
        "6",
        "hitApi",
        "Google Docs: Create keyword research brief document",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://docs.googleapis.com/v1/documents",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "title",
                value: "Keyword Brief: {primary_keyword} — {priority} Priority",
              },
            ],
            variables: [
              {
                name: "documentId",
                customName: "brief_doc_id",
                type: "string",
              },
              { name: "title", customName: "brief_title", type: "string" },
            ],
          },
        },
      ),

      slackNode(
        "7",
        "Slack: Notify content writer of new keyword brief",
        1480,
        ":page_with_curl: *New Keyword Brief Created*\n*Keyword:* {primary_keyword}\n*Volume:* {search_volume}/mo | *Difficulty:* {keyword_difficulty}/100\n*Intent:* {search_intent} | *Format:* {content_format}\n*Opportunity Score:* {opportunity_score} ({priority})\n*Related:* {related_keywords}\n*Doc:* https://docs.google.com/document/d/{brief_doc_id}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log keyword research request and scoring",
        1720,
        "KeywordResearch",
        '[["{primary_keyword}","{search_volume}","{keyword_difficulty}","{search_intent}","{content_format}","{opportunity_score}","{priority}","{brief_doc_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Builds keyword research briefs from topic requests. Pulls keyword data from Ahrefs, uses AI to analyze search intent and competitive gaps, scores opportunity value, creates a Google Docs brief for the content writer, and notifies the team via Slack with full keyword metrics.",
  },

  // ===================================================================
  // 46. Competitor Ad Monitoring Digest — Pattern L
  // ===================================================================
  "Competitor Ad Monitoring Digest": {
    nodes: [
      trigger("Schedule: Daily Competitor Ad Scan at 8AM", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8:00 AM to scan Facebook Ad Library and Google Ads Transparency Center for new competitor ad creatives and campaigns",
        setup:
          "Configure competitor page IDs for Facebook Ad Library and advertiser URLs for Google Ads Transparency. Schedule fires daily. Define your list of tracked competitor brands.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "Facebook Ad Library: Scan competitor ad creatives",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.facebook.com/v18.0/ads_archive?search_terms={competitor_brand}&ad_reached_countries=US&ad_active_status=ACTIVE&fields=ad_creative_body,ad_creative_link_title,page_name,ad_delivery_start_time&limit=20&access_token={fb_token}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            variables: [
              { name: "data", customName: "fb_ads_data", type: "string" },
              {
                name: "data.length",
                customName: "fb_ad_count",
                type: "number",
              },
            ],
          },
        },
      ),

      n(
        "3",
        "hitApi",
        "Google Ads Transparency: Check competitor ad activity",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://adstransparency.google.com/asr/api/v1/advertiser/{competitor_advertiser_id}/creatives?region=US",
            headers: [{ key: "Content-Type", value: "application/json" }],
            variables: [
              {
                name: "creatives",
                customName: "google_ads_data",
                type: "string",
              },
              {
                name: "creatives.length",
                customName: "google_ad_count",
                type: "number",
              },
            ],
          },
        },
      ),

      aiNode(
        "4",
        "OpenAI: Compare competitor ads to our positioning",
        760,
        "You are a competitive intelligence analyst for paid media. Compare competitor ad creatives against our brand positioning. Identify new messaging angles, offers, audience targeting shifts, and creative formats being tested.",
        "Facebook competitor ads: {fb_ads_data}. Google competitor ads: {google_ads_data}. Our positioning: value-driven B2B SaaS. Return JSON with: new_campaign_detected (boolean), competitor_themes (comma-separated), messaging_shifts (string), offer_changes (string), creative_formats (string), threat_level (high/medium/low), recommended_response (string).",
        [
          {
            field: "new_campaign_detected",
            as: "new_campaign_detected",
            type: "boolean",
          },
          {
            field: "competitor_themes",
            as: "competitor_themes",
            type: "string",
          },
          { field: "messaging_shifts", as: "messaging_shifts", type: "string" },
          { field: "offer_changes", as: "offer_changes", type: "string" },
          { field: "threat_level", as: "threat_level", type: "string" },
          {
            field: "recommended_response",
            as: "recommended_response",
            type: "string",
          },
        ],
      ),

      n(
        "5",
        "runCode",
        "Highlight new creatives not seen in previous scans",
        1000,
        {
          codeDetails: {
            language: "javascript",
            code: `const totalAds = parseInt(fb_ad_count || "0") + parseInt(google_ad_count || "0");
const hasNewCampaign = new_campaign_detected === true || new_campaign_detected === "true";
const alert_needed = hasNewCampaign || threat_level === "high";
const digest_summary = "Competitor Ads Scanned: " + totalAds + " | FB: " + (fb_ad_count || 0) + " | Google: " + (google_ad_count || 0) + " | New Campaign: " + hasNewCampaign + " | Threat: " + threat_level;
return { totalAds, alert_needed, digest_summary };`,
            variables: [
              { name: "totalAds", type: "number" },
              { name: "alert_needed", type: "boolean" },
              { name: "digest_summary", type: "string" },
            ],
          },
        },
      ),

      n("6", "condition", "Was a new competitor campaign detected?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "alert_needed", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Alert paid media team of competitor activity",
        1480,
        ":eyes: *Competitor Ad Intelligence*\n*Threat Level:* {threat_level}\n*Ads scanned:* {totalAds}\n*New Campaign:* {new_campaign_detected}\n*Themes:* {competitor_themes}\n*Messaging Shifts:* {messaging_shifts}\n*Offers:* {offer_changes}\n*Recommended Response:* {recommended_response}",
      ),

      emailNode(
        "8",
        "Email: Daily competitor ad digest to marketing team",
        1720,
        "paid-media@company.com",
        "Competitor Ad Digest — Threat Level: {threat_level}",
        "Daily Competitor Ad Monitoring Report\n\n{digest_summary}\n\nCompetitor Themes: {competitor_themes}\nMessaging Shifts: {messaging_shifts}\nOffer Changes: {offer_changes}\nCreative Formats: {creative_formats}\n\nRecommended Response: {recommended_response}\n\nReview competitor creatives and adjust our campaigns if needed.\n\nCompetitive Intelligence Bot",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Daily competitive ad intelligence workflow scanning Facebook Ad Library and Google Ads Transparency for competitor activity. AI compares creatives against your positioning, flags new campaigns and messaging shifts, alerts the paid media team via Slack for high-threat changes, and delivers a comprehensive email digest.",
  },

  // ===================================================================
  // 47. PPC Spend Anomaly Alert — Pattern L
  // ===================================================================
  "PPC Spend Anomaly Alert": {
    nodes: [
      trigger("Schedule: PPC Spend Check Every 4 Hours", "schedule", {
        schedule: "0 */4 * * *",
        howItStarts:
          "Runs every 4 hours to fetch current ad spend from Google Ads and Facebook Ads, compare against daily budgets, and alert on overspend anomalies",
        setup:
          "Configure Google Ads and Facebook Ads API tokens. Set daily budget thresholds per platform in the runCode node. Schedule fires every 4 hours (6 checks per day).",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "Google Ads: Fetch today's spend across campaigns",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
            headers: [
              { key: "Authorization", value: "Bearer {google_ads_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "developer-token", value: "{google_ads_dev_token}" },
            ],
            body: [
              {
                key: "query",
                value:
                  "SELECT metrics.cost_micros, campaign.name, campaign.status FROM campaign WHERE segments.date = TODAY AND campaign.status = 'ENABLED'",
              },
            ],
            variables: [
              {
                name: "results",
                customName: "google_spend_data",
                type: "string",
              },
              {
                name: "results.length",
                customName: "google_campaign_count",
                type: "number",
              },
            ],
          },
        },
      ),

      n(
        "3",
        "hitApi",
        "Facebook Ads: Fetch today's spend across ad sets",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.facebook.com/v18.0/act_{fb_ad_account_id}/insights?fields=spend,campaign_name,impressions&date_preset=today&level=campaign&access_token={fb_token}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            variables: [
              { name: "data", customName: "fb_spend_data", type: "string" },
              {
                name: "data.length",
                customName: "fb_campaign_count",
                type: "number",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "runCode",
        "Compare actual spend against budgets and detect anomalies",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const googleData = JSON.parse(google_spend_data || "[]");
const fbData = JSON.parse(fb_spend_data || "[]");
const googleSpend = googleData.reduce((sum, r) => sum + (parseInt(r.metrics?.cost_micros || 0) / 1000000), 0);
const fbSpend = fbData.reduce((sum, r) => sum + parseFloat(r.spend || 0), 0);
const totalSpend = googleSpend + fbSpend;
const dailyBudget = 500;
const spendRatio = totalSpend / dailyBudget;
const is_overspending = spendRatio > 1.2;
const spend_percent = Math.round(spendRatio * 100);
const anomaly_level = spendRatio > 1.5 ? "CRITICAL" : spendRatio > 1.2 ? "WARNING" : "NORMAL";
return { googleSpend: googleSpend.toFixed(2), fbSpend: fbSpend.toFixed(2), totalSpend: totalSpend.toFixed(2), spend_percent, is_overspending, anomaly_level };`,
            variables: [
              { name: "googleSpend", type: "string" },
              { name: "fbSpend", type: "string" },
              { name: "totalSpend", type: "string" },
              { name: "spend_percent", type: "number" },
              { name: "is_overspending", type: "boolean" },
              { name: "anomaly_level", type: "string" },
            ],
          },
        },
      ),

      n(
        "5",
        "condition",
        "Is total spend exceeding 120% of daily budget?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "is_overspending", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Urgent overspend alert to media buying team",
        1240,
        ":rotating_light: *PPC Spend Anomaly — {anomaly_level}*\n*Total Spend Today:* ${totalSpend} ({spend_percent}% of budget)\n*Google Ads:* ${googleSpend}\n*Facebook Ads:* ${fbSpend}\n*Campaigns:* Google({google_campaign_count}) + FB({fb_campaign_count})\n\n:point_right: Review campaigns immediately and pause overperformers if needed.",
      ),

      emailNode(
        "7",
        "Email: Alert media buyer of spend anomaly",
        1480,
        "media-buyer@company.com",
        "PPC SPEND ALERT — {anomaly_level}: ${totalSpend} spent ({spend_percent}% of budget)",
        "URGENT: PPC Spend Anomaly Detected\n\nAnomaly Level: {anomaly_level}\nTotal Spend Today: ${totalSpend}\nBudget Utilization: {spend_percent}%\n\nBreakdown:\n- Google Ads: ${googleSpend} ({google_campaign_count} campaigns)\n- Facebook Ads: ${fbSpend} ({fb_campaign_count} campaigns)\n\nPlease review campaign performance and take corrective action.\n\nPPC Monitoring Bot",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log spend check for trend analysis",
        1720,
        "PPCSpendMonitor",
        '[["{totalSpend}","{googleSpend}","{fbSpend}","{spend_percent}","{anomaly_level}","{google_campaign_count}","{fb_campaign_count}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("5", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Monitors PPC spend across Google Ads and Facebook Ads every 4 hours. Compares actual spend against daily budgets, detects overspend anomalies, triggers urgent Slack and email alerts when spend exceeds 120% of budget, and logs all spend data to Google Sheets for trend analysis.",
  },

  // ===================================================================
  // 48. LinkedIn Post Queue Publisher — Pattern I
  // ===================================================================
  "LinkedIn Post Queue Publisher": {
    nodes: [
      trigger("Airtable Webhook: LinkedIn Post Approved", "webhook_app", {
        source: "Airtable",
        event: "record.status_changed",
        howItStarts:
          "Fires when a LinkedIn post in the content queue Airtable base is marked as 'Approved', ready for scheduling and publishing",
        setup:
          "In Airtable, configure an automation that triggers a webhook when a record's Status field changes to 'Approved'. Include post text, media URL (if any), post type (text/image/article), and scheduled publish time in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract and validate post content details",
        280,
        "You are a LinkedIn content manager. Parse the Airtable webhook to extract post content details. Validate that the post meets LinkedIn best practices: appropriate length, includes a hook in the first line, has a call-to-action.",
        "Extract from Airtable webhook: {{{1.body}}}. Return JSON with: post_text (string), post_type (text/image/article), media_url (string or empty), scheduled_time (ISO datetime), author_name (string), has_media (boolean), character_count (number), meets_best_practices (boolean).",
        [
          { field: "post_text", as: "post_text", type: "string" },
          { field: "post_type", as: "post_type", type: "string" },
          { field: "media_url", as: "media_url", type: "string" },
          { field: "scheduled_time", as: "scheduled_time", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "has_media", as: "has_media", type: "boolean" },
          { field: "character_count", as: "character_count", type: "number" },
        ],
      ),

      n("3", "runCode", "Check posting schedule and optimal timing", 520, {
        codeDetails: {
          language: "javascript",
          code: `const scheduledDate = new Date(scheduled_time || "");
const hour = scheduledDate.getUTCHours();
const day = scheduledDate.getUTCDay();
const isOptimalTime = hour >= 8 && hour <= 10 && day >= 1 && day <= 5;
const schedule_note = isOptimalTime ? "Optimal posting window (Tue-Fri 8-10AM)" : "Outside peak engagement — consider rescheduling to weekday 8-10AM";
const ready_to_publish = character_count > 50 && character_count < 3000;
return { isOptimalTime, schedule_note, ready_to_publish };`,
          variables: [
            { name: "isOptimalTime", type: "boolean" },
            { name: "schedule_note", type: "string" },
            { name: "ready_to_publish", type: "boolean" },
          ],
        },
      }),

      n("4", "hitApi", "LinkedIn: Publish post via API", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "author", value: "urn:li:person:{linkedin_person_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value:
                '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{post_text}"},"shareMediaCategory":"NONE"}}',
            },
            {
              key: "visibility",
              value: '{"com.linkedin.ugc.MemberNetworkVisibility":"PUBLIC"}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_post_id", type: "string" },
            {
              name: "lifecycleState",
              customName: "post_status",
              type: "string",
            },
          ],
        },
      }),

      n("5", "condition", "Does the post include media to upload?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_media", operator: "equals", value: "true" },
          ],
        },
      }),

      n("6", "hitApi", "LinkedIn: Upload media asset for post", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/assets?action=registerUpload",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "registerUploadRequest",
              value:
                '{"recipes":["urn:li:digitalmediaRecipe:feedshare-image"],"owner":"urn:li:person:{linkedin_person_id}","serviceRelationships":[{"relationshipType":"OWNER","identifier":"urn:li:userGeneratedContent"}]}',
            },
          ],
          variables: [
            {
              name: "value.asset",
              customName: "media_asset_urn",
              type: "string",
            },
            {
              name: "value.uploadMechanism",
              customName: "upload_url",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Confirm LinkedIn post published successfully",
        1480,
        ":linkedin: *LinkedIn Post Published*\n*Author:* {author_name}\n*Type:* {post_type}\n*Characters:* {character_count}\n*Media:* {has_media}\n*Schedule:* {schedule_note}\n*Post ID:* {linkedin_post_id}\n*Status:* {post_status}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log LinkedIn publishing activity",
        1720,
        "LinkedInPosts",
        '[["{author_name}","{post_type}","{character_count}","{has_media}","{linkedin_post_id}","{isOptimalTime}","{schedule_note}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Publishes approved LinkedIn posts from an Airtable content queue. Extracts post details via AI, checks optimal posting timing, publishes via the LinkedIn API, handles media uploads for image posts, confirms publication via Slack, and logs all activity to Google Sheets.",
  },

  // ===================================================================
  // 49. Instagram Sentiment Monitor — Pattern L
  // ===================================================================
  "Instagram Sentiment Monitor": {
    nodes: [
      trigger("Schedule: Daily Instagram Comment Sentiment Scan", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9:00 AM to fetch recent Instagram comments via the Graph API, analyze sentiment per post, and alert the social team on negative trends",
        setup:
          "Configure Instagram Graph API access with a valid page token. Ensure the token has permissions to read comments on business posts. Define the Instagram business account ID.",
        inputFields: [],
      }),

      n("2", "hitApi", "Instagram Graph API: Fetch recent post comments", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/{ig_business_account_id}/media?fields=id,caption,comments{text,username,timestamp}&limit=10&access_token={ig_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          variables: [
            { name: "data", customName: "posts_with_comments", type: "string" },
            { name: "data.length", customName: "post_count", type: "number" },
          ],
        },
      }),

      aiNode(
        "3",
        "OpenAI: Classify sentiment for comments on each post",
        520,
        "You are a social media sentiment analyst specializing in Instagram. Classify each comment as positive, negative, or neutral. Identify spam, calculate per-post sentiment scores, and flag any posts with unusually negative comment sections.",
        "Analyze Instagram comments: {posts_with_comments}. Return JSON with: total_comments (number), positive_pct (number 0-100), negative_pct (number 0-100), neutral_pct (number 0-100), worst_post_id (string ID of post with lowest sentiment), worst_post_sentiment (number -100 to 100), flagged_comments (string listing concerning comments), overall_trend (improving/stable/declining).",
        [
          { field: "total_comments", as: "total_comments", type: "number" },
          { field: "positive_pct", as: "positive_pct", type: "number" },
          { field: "negative_pct", as: "negative_pct", type: "number" },
          { field: "worst_post_id", as: "worst_post_id", type: "string" },
          {
            field: "worst_post_sentiment",
            as: "worst_post_sentiment",
            type: "number",
          },
          { field: "flagged_comments", as: "flagged_comments", type: "string" },
          { field: "overall_trend", as: "overall_trend", type: "string" },
        ],
      ),

      n(
        "4",
        "runCode",
        "Compute sentiment trend and detect negative shifts",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const negPct = parseInt(negative_pct || "0");
const posPct = parseInt(positive_pct || "0");
const negative_trend = negPct > 30 || overall_trend === "declining";
const sentiment_score = posPct - negPct;
const health_status = sentiment_score > 40 ? "HEALTHY" : sentiment_score > 0 ? "WATCH" : "CONCERNING";
return { negative_trend, sentiment_score, health_status };`,
            variables: [
              { name: "negative_trend", type: "boolean" },
              { name: "sentiment_score", type: "number" },
              { name: "health_status", type: "string" },
            ],
          },
        },
      ),

      n(
        "5",
        "condition",
        "Is there a negative sentiment trend on Instagram?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "negative_trend", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Alert social team of negative Instagram sentiment",
        1240,
        ":warning: *Instagram Sentiment Alert*\n*Status:* {health_status}\n*Score:* {sentiment_score}\n*Trend:* {overall_trend}\n*Comments analyzed:* {total_comments}\n*Positive:* {positive_pct}% | *Negative:* {negative_pct}%\n*Worst post:* {worst_post_id} (score: {worst_post_sentiment})\n*Flagged:* {flagged_comments}",
      ),

      emailNode(
        "7",
        "Email: Instagram sentiment report to social media manager",
        1480,
        "social-manager@company.com",
        "Instagram Sentiment Report — {health_status}",
        "Daily Instagram Sentiment Report\n\nHealth Status: {health_status}\nSentiment Score: {sentiment_score}\nOverall Trend: {overall_trend}\n\nComments Analyzed: {total_comments}\n- Positive: {positive_pct}%\n- Negative: {negative_pct}%\n\nWorst Performing Post: {worst_post_id}\nWorst Post Sentiment: {worst_post_sentiment}\n\nFlagged Comments:\n{flagged_comments}\n\nPlease review and respond to flagged comments.\n\nSocial Monitoring Bot",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log daily Instagram sentiment metrics",
        1720,
        "InstagramSentiment",
        '[["{post_count}","{total_comments}","{positive_pct}","{negative_pct}","{sentiment_score}","{health_status}","{overall_trend}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors Instagram post comment sentiment daily. Fetches recent comments via the Graph API, uses AI to classify sentiment and flag concerning comments, detects negative trends, alerts the social team via Slack when sentiment declines, and emails the social media manager a comprehensive report.",
  },

  // ===================================================================
  // 50. TikTok UGC Vetting Workflow — Pattern I
  // ===================================================================
  "TikTok UGC Vetting Workflow": {
    nodes: [
      trigger("Webhook: UGC Content Submitted for Brand Use", "webhook_app", {
        source: "TikTok",
        event: "ugc.submitted",
        howItStarts:
          "Fires when a creator submits user-generated content (UGC) for potential brand use, delivering the video URL, creator details, and submission context",
        setup:
          "Set up a webhook endpoint that receives UGC submissions from your creator portal or TikTok Creator Marketplace. Include video URL, creator handle, creator follower count, content description, and submission timestamp in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Analyze UGC content for brand safety",
        280,
        "You are a brand safety and content review specialist. Analyze submitted UGC for brand alignment, safety concerns, content quality, and messaging accuracy. Flag any issues with profanity, competitor mentions, misleading claims, or off-brand tone.",
        "Analyze this UGC submission: {{{1.body}}}. Return JSON with: creator_handle, creator_followers (number), video_url, content_description, brand_safe (boolean), safety_issues (string listing any concerns), content_quality (high/medium/low), on_brief (boolean), brief_alignment_score (0-100).",
        [
          { field: "creator_handle", as: "creator_handle", type: "string" },
          {
            field: "creator_followers",
            as: "creator_followers",
            type: "number",
          },
          { field: "video_url", as: "video_url", type: "string" },
          { field: "brand_safe", as: "brand_safe", type: "boolean" },
          { field: "safety_issues", as: "safety_issues", type: "string" },
          { field: "content_quality", as: "content_quality", type: "string" },
          { field: "on_brief", as: "on_brief", type: "boolean" },
          {
            field: "brief_alignment_score",
            as: "brief_alignment_score",
            type: "number",
          },
        ],
      ),

      n("3", "runCode", "Check creator history and past performance", 520, {
        codeDetails: {
          language: "javascript",
          code: `const followers = parseInt(creator_followers || "0");
const isMicroInfluencer = followers >= 1000 && followers < 100000;
const isMacroInfluencer = followers >= 100000;
const creator_tier = isMacroInfluencer ? "macro" : isMicroInfluencer ? "micro" : "nano";
const qualityOk = content_quality === "high" || content_quality === "medium";
const passes_vetting = brand_safe === true && on_brief === true && qualityOk;
const vetting_summary = passes_vetting ? "PASS — brand safe and on brief" : "FAIL — " + (brand_safe ? "" : "brand safety concern; ") + (on_brief ? "" : "off brief; ") + (qualityOk ? "" : "low quality");
return { creator_tier, passes_vetting, vetting_summary };`,
          variables: [
            { name: "creator_tier", type: "string" },
            { name: "passes_vetting", type: "boolean" },
            { name: "vetting_summary", type: "string" },
          ],
        },
      }),

      n(
        "4",
        "condition",
        "Does UGC pass brand safety and brief alignment?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "passes_vetting", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      n(
        "5",
        "approvalGate",
        "Brand manager: Final review of vetted UGC",
        1000,
        {
          approvalDetails: {
            approvers: ["brand-manager@company.com"],
            message:
              "UGC Submission for Review:\n\nCreator: @{creator_handle} ({creator_tier}, {creator_followers} followers)\nVideo: {video_url}\nBrand Safe: {brand_safe}\nOn Brief: {on_brief} ({brief_alignment_score}/100)\nQuality: {content_quality}\nVetting: {vetting_summary}\nSafety Issues: {safety_issues}\n\nPlease approve for brand use or reject with feedback.",
          },
        },
      ),

      n("6", "hitApi", "Airtable: Update UGC submission status", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{base_id}/UGC%20Submissions",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"Creator":"@{creator_handle}","Tier":"{creator_tier}","Followers":"{creator_followers}","Video URL":"{video_url}","Brand Safe":"{brand_safe}","Vetting":"{vetting_summary}","Status":"Reviewed"}',
            },
          ],
          variables: [
            { name: "id", customName: "airtable_record_id", type: "string" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Notify creative team of UGC review outcome",
        1480,
        ":movie_camera: *UGC Review Complete*\n*Creator:* @{creator_handle} ({creator_tier})\n*Followers:* {creator_followers}\n*Vetting:* {vetting_summary}\n*Quality:* {content_quality}\n*Brief Score:* {brief_alignment_score}/100\n*Video:* {video_url}",
      ),

      emailNode(
        "8",
        "Email: Send feedback to creator on submission",
        1720,
        "{creator_handle}@creator-portal.com",
        "Your UGC submission has been reviewed — @{creator_handle}",
        "Hi @{creator_handle},\n\nThank you for your UGC submission! Our brand team has reviewed your content.\n\nVideo: {video_url}\nBrand Safety: {brand_safe}\nBrief Alignment: {brief_alignment_score}/100\nContent Quality: {content_quality}\n\nVetting Result: {vetting_summary}\n\nIf your content was approved, our team will reach out with next steps for usage rights and compensation. If adjustments are needed, please check the notes above and resubmit.\n\nThank you for creating with us!\nBrand Partnerships Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Vets TikTok UGC submissions for brand safety and brief alignment. AI analyzes content quality and safety, checks creator history and tier, routes passing submissions through brand manager approval, updates Airtable tracking, notifies the creative team via Slack, and sends feedback to the creator.",
  },

  // ===================================================================
  // 51. Weekly Marketing KPI Digest — Pattern I
  // ===================================================================
  "Weekly Marketing KPI Digest": {
    nodes: [
      trigger("Schedule: Friday 5PM Weekly KPI Compilation", "schedule", {
        schedule: "0 17 * * 5",
        howItStarts:
          "Runs every Friday at 5:00 PM to compile marketing KPIs from HubSpot and Google Analytics, generate an AI narrative summary, and distribute to leadership",
        setup:
          "Configure HubSpot API token for pipeline metrics and Google Analytics Data API access for traffic data. Schedule fires every Friday at 5PM. Define the GA4 property ID and HubSpot pipeline ID.",
        inputFields: [],
      }),

      n("2", "hitApi", "HubSpot: Fetch weekly pipeline and lead metrics", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"createdate","operator":"GTE","value":"{week_start_epoch}"}]}]',
            },
            { key: "limit", value: "0" },
          ],
          variables: [
            {
              name: "total",
              customName: "new_leads_this_week",
              type: "number",
            },
          ],
        },
      }),

      n(
        "3",
        "hitApi",
        "Google Analytics: Fetch weekly traffic and conversion data",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "dateRanges",
                value: '[{"startDate":"7daysAgo","endDate":"today"}]',
              },
              {
                key: "metrics",
                value:
                  '[{"name":"sessions"},{"name":"conversions"},{"name":"totalUsers"},{"name":"bounceRate"}]',
              },
              {
                key: "dimensions",
                value: '[{"name":"sessionDefaultChannelGroup"}]',
              },
            ],
            variables: [
              { name: "rows", customName: "ga_weekly_data", type: "string" },
              {
                name: "rowCount",
                customName: "ga_channel_count",
                type: "number",
              },
            ],
          },
        },
      ),

      aiNode(
        "4",
        "OpenAI: Generate narrative summary of marketing performance",
        760,
        "You are a marketing analytics director. Given weekly HubSpot pipeline data and Google Analytics traffic data, write a concise executive narrative highlighting wins, concerns, and recommended actions for next week. Be specific with numbers.",
        "Weekly data — New leads: {new_leads_this_week}. GA data by channel: {ga_weekly_data}. Return JSON with: headline (one-line performance summary), wins (string listing top 2-3 wins), concerns (string listing any red flags), recommended_actions (string with 2-3 action items), overall_grade (A/B/C/D/F), total_sessions (number), total_conversions (number), conversion_rate (number as percentage).",
        [
          { field: "headline", as: "kpi_headline", type: "string" },
          { field: "wins", as: "kpi_wins", type: "string" },
          { field: "concerns", as: "kpi_concerns", type: "string" },
          { field: "recommended_actions", as: "kpi_actions", type: "string" },
          { field: "overall_grade", as: "overall_grade", type: "string" },
          { field: "total_sessions", as: "total_sessions", type: "number" },
          {
            field: "total_conversions",
            as: "total_conversions",
            type: "number",
          },
        ],
      ),

      n("5", "runCode", "Format KPI dashboard for distribution", 1000, {
        codeDetails: {
          language: "javascript",
          code: `const dashboard = [
  "WEEKLY MARKETING KPI REPORT",
  "Grade: " + overall_grade,
  "",
  "Leads: " + new_leads_this_week,
  "Sessions: " + total_sessions,
  "Conversions: " + total_conversions,
  "",
  "Wins: " + kpi_wins,
  "Concerns: " + kpi_concerns,
  "Actions: " + kpi_actions
].join("\\n");
const report_ready = true;
return { formatted_dashboard: dashboard, report_ready };`,
          variables: [
            { name: "formatted_dashboard", type: "string" },
            { name: "report_ready", type: "boolean" },
          ],
        },
      }),

      emailNode(
        "6",
        "Email: Send weekly KPI digest to leadership",
        1240,
        "leadership@company.com",
        "Weekly Marketing KPI Digest — Grade: {overall_grade} | {kpi_headline}",
        "Hi Leadership Team,\n\n{kpi_headline}\n\n{formatted_dashboard}\n\nWins This Week:\n{kpi_wins}\n\nConcerns:\n{kpi_concerns}\n\nRecommended Actions for Next Week:\n{kpi_actions}\n\nFull dashboard available in Google Sheets.\n\nHave a great weekend!\nMarketing Analytics Bot",
      ),

      slackNode(
        "7",
        "Slack: Post weekly highlights to #marketing channel",
        1480,
        ":bar_chart: *Weekly Marketing KPI Digest*\n*Grade:* {overall_grade}\n*{kpi_headline}*\n\n*Leads:* {new_leads_this_week} | *Sessions:* {total_sessions} | *Conversions:* {total_conversions}\n\n:trophy: *Wins:* {kpi_wins}\n:warning: *Concerns:* {kpi_concerns}\n:dart: *Actions:* {kpi_actions}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log weekly KPI metrics for trending",
        1720,
        "WeeklyKPIs",
        '[["{overall_grade}","{new_leads_this_week}","{total_sessions}","{total_conversions}","{kpi_headline}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Compiles a weekly marketing KPI digest every Friday from HubSpot pipeline metrics and Google Analytics traffic data. AI generates an executive narrative with wins, concerns, and action items, then distributes the report to leadership via email and Slack with historical logging in Google Sheets.",
  },

  // ===================================================================
  // 52. Webinar Reminder + Attendance Follow-Up — Pattern I
  // ===================================================================
  "Webinar Reminder + Attendance Follow-Up": {
    nodes: [
      trigger("Zoom Webhook: Webinar Registration Received", "webhook_app", {
        source: "Zoom",
        event: "webinar.registration_created",
        howItStarts:
          "Fires when someone registers for a Zoom webinar, capturing registrant details, webinar topic, and scheduled date for automated reminder and follow-up sequences",
        setup:
          "In Zoom Marketplace > Your App > Event Subscriptions, subscribe to the 'webinar.registration_created' event. Include registrant email, name, webinar ID, topic, start time, and any custom registration fields in the payload.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "HubSpot: Update contact with webinar registration",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{registrant_email}","firstname":"{registrant_name}","webinar_registered":"true","webinar_topic":"{webinar_topic}","webinar_date":"{webinar_start_time}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "properties.email",
                customName: "registrant_email",
                type: "string",
              },
            ],
          },
        },
      ),

      n("3", "delayAction", "Wait until 1 day before the webinar", 520, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      emailNode(
        "4",
        "Email: Send webinar reminder with join link",
        760,
        "{registrant_email}",
        "Reminder: {webinar_topic} starts tomorrow — don't miss it!",
        "Hi {registrant_name},\n\nJust a friendly reminder that the webinar you registered for is happening tomorrow!\n\nTopic: {webinar_topic}\nDate & Time: {webinar_start_time}\nJoin Link: {webinar_join_url}\n\nPro tips to get the most out of the session:\n- Prepare 1-2 questions for the Q&A\n- Have a notebook ready for key takeaways\n- Plan to arrive 5 minutes early to test your audio\n\nSee you there!\nThe Events Team",
      ),

      n("5", "hitApi", "Zoom: Check attendee status after webinar ends", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/past_webinars/{webinar_id}/participants?page_size=100",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          variables: [
            {
              name: "participants",
              customName: "attendee_list",
              type: "string",
            },
            {
              name: "total_records",
              customName: "total_attendees",
              type: "number",
            },
          ],
        },
      }),

      n("6", "condition", "Did the registrant attend the webinar?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "total_attendees", operator: "greaterThan", value: "0" },
          ],
        },
      }),

      emailNode(
        "7",
        "Email: Send recording and resources to attendees",
        1480,
        "{registrant_email}",
        "Thanks for attending {webinar_topic} — recording + resources inside",
        "Hi {registrant_name},\n\nThank you for joining our webinar on {webinar_topic}! Here's everything you need:\n\nRecording: {webinar_recording_url}\nSlide Deck: {webinar_slides_url}\nResource Guide: {webinar_resources_url}\n\nKey takeaways from the session:\n1. {key_takeaway_1}\n2. {key_takeaway_2}\n3. {key_takeaway_3}\n\nWant to dive deeper? Book a 1:1 consultation with our team.\n\nBest,\nThe Events Team",
      ),

      emailNode(
        "8",
        "Email: Send replay link to no-show registrants",
        1720,
        "{registrant_email}",
        "You missed {webinar_topic} — watch the replay now",
        "Hi {registrant_name},\n\nWe missed you at the {webinar_topic} webinar! Don't worry — we recorded it for you.\n\nWatch the Replay: {webinar_recording_url}\nSlide Deck: {webinar_slides_url}\n\nThe session covered practical strategies you can implement right away. The replay is available for the next 7 days.\n\nStill have questions? Reply to this email and we'll connect you with our team.\n\nBest,\nThe Events Team",
        340,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
    summary:
      "Manages the full webinar lifecycle from registration to follow-up. Updates HubSpot on registration, sends a 1-day-before reminder email, checks Zoom attendance after the event, and delivers tailored follow-ups: recording plus resources for attendees, or a replay link for no-shows.",
  },

  // ===================================================================
  // 53. Nurture Sequence Enrollment by Segment — Pattern K
  // ===================================================================
  "Nurture Sequence Enrollment by Segment": {
    nodes: [
      trigger(
        "HubSpot Webhook: Contact Lifecycle Stage Changed",
        "webhook_app",
        {
          source: "HubSpot",
          event: "contact.propertyChange",
          howItStarts:
            "Fires when a contact's lifecycle stage changes in HubSpot (e.g., subscriber to lead, lead to MQL), triggering segment-based nurture sequence enrollment",
          setup:
            "In HubSpot > Workflows or webhook subscriptions, configure a trigger for lifecycle stage property changes. Include contact ID, email, new lifecycle stage, previous stage, and any product interest properties in the payload.",
          inputFields: [],
        },
      ),

      n(
        "2",
        "runCode",
        "Determine nurture segment based on lifecycle and interest",
        280,
        {
          codeDetails: {
            language: "javascript",
            code: `const body = JSON.parse(trigger_body || "{}");
const contact_email = body.properties?.email || body.email || "";
const contact_name = body.properties?.firstname || body.name || "";
const new_stage = body.properties?.lifecyclestage || body.lifecyclestage || "";
const product_interest = body.properties?.product_interest || "";
const has_product_interest = product_interest.length > 0;
const segment = new_stage === "marketingqualifiedlead" ? "mql_nurture" : new_stage === "lead" && has_product_interest ? "product_nurture" : new_stage === "lead" ? "general_nurture" : "awareness_nurture";
const sequence_id = segment === "mql_nurture" ? "seq_mql_001" : segment === "product_nurture" ? "seq_prod_001" : segment === "general_nurture" ? "seq_gen_001" : "seq_aware_001";
return { contact_email, contact_name, new_stage, product_interest, has_product_interest, segment, sequence_id };`,
            variables: [
              { name: "contact_email", type: "string" },
              { name: "contact_name", type: "string" },
              { name: "new_stage", type: "string" },
              { name: "product_interest", type: "string" },
              { name: "has_product_interest", type: "boolean" },
              { name: "segment", type: "string" },
              { name: "sequence_id", type: "string" },
            ],
          },
        },
      ),

      n("3", "condition", "Is the contact's product interest known?", 520, {
        conditionDetails: {
          conditions: [
            {
              field: "has_product_interest",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      n(
        "4",
        "hitApi",
        "HubSpot: Enroll contact in appropriate nurture sequence",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/automation/v2/workflows/{workflow_id}/enrollments/contacts/{contact_email}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "enrolled",
                customName: "enrollment_success",
                type: "boolean",
              },
            ],
          },
        },
      ),

      slackNode(
        "5",
        "Slack: Notify marketing of sequence enrollment",
        1000,
        ":envelope: *Nurture Sequence Enrollment*\n*Contact:* {contact_name} ({contact_email})\n*Stage:* {new_stage}\n*Segment:* {segment}\n*Product Interest:* {product_interest}\n*Sequence:* {sequence_id}\n*Enrolled:* {enrollment_success}",
      ),

      emailNode(
        "6",
        "Email: First touch — segment-appropriate nurture content",
        1240,
        "{contact_email}",
        "Welcome to the journey, {contact_name} — here's what's next",
        "Hi {contact_name},\n\nWe're excited to share resources tailored to your interests.\n\nBased on your profile, we've curated a learning path that covers:\n\n- Getting started with {product_interest}\n- Best practices and expert tips\n- Real customer success stories\n\nYour first resource is ready — check it out here: [Resource Link]\n\nWe'll send you helpful content over the coming weeks. You can adjust your preferences anytime.\n\nBest,\nThe Content Team",
      ),

      sheetsNode(
        "7",
        "Google Sheets: Log nurture enrollment for tracking",
        1480,
        "NurtureEnrollments",
        '[["{contact_email}","{contact_name}","{new_stage}","{segment}","{sequence_id}","{product_interest}","{enrollment_success}","=NOW()"]]',
      ),

      n(
        "8",
        "runCode",
        "Suppress conflicting sequences to avoid overlap",
        1720,
        {
          codeDetails: {
            language: "javascript",
            code: `const conflictSequences = ["seq_gen_001","seq_aware_001","seq_prod_001","seq_mql_001"];
const toSuppress = conflictSequences.filter(s => s !== sequence_id);
const suppressed_count = toSuppress.length;
const suppressed_sequences = toSuppress.join(", ");
const suppression_applied = true;
return { suppressed_count, suppressed_sequences, suppression_applied };`,
            variables: [
              { name: "suppressed_count", type: "number" },
              { name: "suppressed_sequences", type: "string" },
              { name: "suppression_applied", type: "boolean" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically enrolls contacts in the right nurture sequence when their HubSpot lifecycle stage changes. Segments contacts by stage and product interest, enrolls them in the appropriate sequence, sends a tailored first-touch email, logs enrollment to Google Sheets, and suppresses conflicting sequences to prevent overlap.",
  },
};
